import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

interface GuideCard {
  slug: string;
  image: string;
  titleKey: string;
  descKey: string;
  icon: string;
}

const guides: GuideCard[] = [
  {
    slug: "usfans-review",
    image:
      "https://readdy.ai/api/search-image?query=A%20neat%20desk%20scene%20with%20a%20tablet%20showing%20a%20review%20page%20beside%20a%20coffee%20cup%20and%20a%20folded%20grey%20sweater%2C%20soft%20morning%20light%2C%20minimal%20editorial%20photography%2C%20neutral%20grey%20and%20cream%20tones%2C%20high%20detail&width=800&height=500&seq=usfans-guide-review&orientation=landscape",
    titleKey: "guides.1.title",
    descKey: "guides.1.desc",
    icon: "ri-star-line",
  },
  {
    slug: "usfans-shipping-cost",
    image:
      "https://readdy.ai/api/search-image?query=A%20stack%20of%20cardboard%20shipping%20boxes%20with%20a%20parcel%20scale%20and%20packing%20tape%20on%20a%20light%20wooden%20table%2C%20soft%20natural%20light%2C%20minimal%20editorial%20logistics%20photography%2C%20warm%20neutral%20tones%2C%20high%20detail&width=800&height=500&seq=usfans-guide-shipping&orientation=landscape",
    titleKey: "guides.2.title",
    descKey: "guides.2.desc",
    icon: "ri-flight-takeoff-line",
  },
  {
    slug: "usfans-coupon",
    image:
      "https://readdy.ai/api/search-image?query=A%20smartphone%20showing%20a%20discount%20code%20screen%20beside%20a%20small%20gift%20box%20and%20a%20wallet%20on%20a%20soft%20pastel%20background%2C%20gentle%20daylight%2C%20minimal%20editorial%20ecommerce%20photography%2C%20fresh%20mint%20and%20cream%20tones%2C%20high%20detail&width=800&height=500&seq=usfans-guide-coupon&orientation=landscape",
    titleKey: "guides.3.title",
    descKey: "guides.3.desc",
    icon: "ri-coupon-3-line",
  },
  {
    slug: "usfans-coupon",
    image:
      "https://readdy.ai/api/search-image?query=A%20close%20up%20of%20a%20hand%20holding%20a%20credit%20card%20next%20to%20a%20discount%20tag%20and%20a%20laptop%20showing%20a%20promo%20code%20field%2C%20soft%20natural%20light%2C%20minimal%20editorial%20commerce%20photography%2C%20warm%20neutral%20tones%2C%20high%20detail&width=800&height=500&seq=usfans-guide-discount&orientation=landscape",
    titleKey: "guides.4.title",
    descKey: "guides.4.desc",
    icon: "ri-percent-line",
  },
  {
    slug: "usfans-warehouse",
    image:
      "https://readdy.ai/api/search-image?query=An%20organized%20warehouse%20interior%20with%20neat%20shelves%20of%20labeled%20boxes%20and%20a%20worker%20holding%20a%20scanner%20in%20the%20distance%2C%20soft%20ambient%20light%2C%20clean%20modern%20editorial%20logistics%20photography%2C%20cool%20grey%20and%20warm%20wood%20tones%2C%20high%20detail&width=800&height=500&seq=usfans-guide-warehouse&orientation=landscape",
    titleKey: "guides.5.title",
    descKey: "guides.5.desc",
    icon: "ri-building-2-line",
  },
  {
    slug: "usfans-legit",
    image:
      "https://readdy.ai/api/search-image?query=A%20close%20up%20of%20a%20hand%20holding%20a%20smartphone%20showing%20a%20secure%20website%20lock%20icon%20beside%20a%20notebook%20with%20a%20checklist%2C%20soft%20natural%20light%2C%20minimal%20editorial%20security%20themed%20photography%2C%20calm%20blue%20grey%20and%20white%20tones%2C%20high%20detail&width=800&height=500&seq=usfans-guide-legit&orientation=landscape",
    titleKey: "guides.6.title",
    descKey: "guides.6.desc",
    icon: "ri-shield-check-line",
  },
];

export default function GuideCards() {
  const { t, i18n } = useTranslation();

  return (
    <section id="guides" className="w-full bg-background-100">
      <div className="mx-auto max-w-6xl px-4 md:px-6 py-14 md:py-20">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
            {t("guides.title")}
          </h2>
          <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
            {t("guides.subtitle")}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {guides.map((guide) => (
            <Link
              key={guide.titleKey}
              to={`/${i18n.language}/article/${guide.slug}`}
              className="group flex flex-col overflow-hidden rounded-2xl border border-background-200 bg-background-50 hover:border-primary-300 transition-all cursor-pointer"
            >
              <div className="relative h-40 overflow-hidden">
                <img
                  src={guide.image}
                  alt={t(guide.titleKey)}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <span className="absolute top-3 left-3 w-9 h-9 rounded-full bg-background-50/90 flex items-center justify-center text-primary-600">
                  <i className={`${guide.icon} text-lg`}></i>
                </span>
              </div>
              <div className="flex flex-col flex-1 p-5">
                <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950 leading-snug">
                  {t(guide.titleKey)}
                </h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed flex-1">
                  {t(guide.descKey)}
                </p>
                <span className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-primary-600">
                  {t("guides.learnMore")}
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-sm"></i>
                  </span>
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}